import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Heart, BellRing, ShoppingBag, Loader2, Star, Flame,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SEO from "../components/SEO";

// -----------------------------------------------------------
// Color swatches — hex + on-color text — used everywhere:
//   - placeholder image generation (dynamic per-color)
//   - color-chip dots in the variant picker
//   - "Selected color" preview badge
// -----------------------------------------------------------
const COLOR_SWATCH = {
    "Black":         { bg: "#0a0a0a", ink: "#f5f5f4", tag: "0a0a0a", inkTag: "f5f5f4" },
    "White":         { bg: "#f5f5f4", ink: "#0a0a0a", tag: "f5f5f4", inkTag: "0a0a0a" },
    "Navy Blue":     { bg: "#1e3a5f", ink: "#e0e7ff", tag: "1e3a5f", inkTag: "e0e7ff" },
    "Heather Grey":  { bg: "#9ca3af", ink: "#111827", tag: "9ca3af", inkTag: "111827" },
    "Forest Green":  { bg: "#22543d", ink: "#d1fae5", tag: "22543d", inkTag: "d1fae5" },
};

// Product-specific metadata (badge, tagline). Descriptions come from backend `material` field.
const PRODUCT_META = {
    // --- Original 7 ---
    "tee":          { tagline: "Truth. On your chest.",              badge: "Bestseller"    },
    "mug":          { tagline: "Morning coffee, verified.",          badge: null            },
    "stickers":     { tagline: "Compass star + verdict badges.",     badge: "Fan favorite"  },
    "light-hoodie": { tagline: "Beach-night ready.",                 badge: null            },
    "heavy-hoodie": { tagline: "Built for cold facts.",              badge: "Winter drop"   },
    "beanie":       { tagline: "Cozy up with the truth.",            badge: null            },
    "cap":          { tagline: "Embroidered compass star. Signature.", badge: "Embroidered"  },
    // --- 13 NEW ---
    "long-sleeve":  { tagline: "Layered truth, all season.",         badge: "New"           },
    "tank":         { tagline: "Summer heat. Cold facts.",           badge: "New"           },
    "crewneck":     { tagline: "Weekend-cozy, weekday-honest.",      badge: "New"           },
    "zip-hoodie":   { tagline: "Zip up. Speak up.",                  badge: "New"           },
    "trucker":      { tagline: "Mesh back, foam front, no lies.",    badge: "New"           },
    "dad-hat":      { tagline: "Soft crown. Sharp mind.",            badge: "New"           },
    "bucket-hat":   { tagline: "Full-brim, full clarity.",           badge: "New"           },
    "tote":         { tagline: "Carry the truth, everywhere.",       badge: "New"           },
    "bottle":       { tagline: "Hydrate. Verify. Repeat.",           badge: "New"           },
    "pin":          { tagline: "Small pin, loud statement.",         badge: "New"           },
    "poster":       { tagline: "Wall art for the wide-awake.",       badge: "New"           },
    "journal":      { tagline: "Where your fact-checks live.",       badge: "New"           },
    "deck":         { tagline: "Truth night, cards & coasters.",     badge: "New"           },
};

// -----------------------------------------------------------
// Mockup image lookup — 35 real product photos live in /public/.
// Filename convention: mockup-{productId}-{colorSlug}.png
// -----------------------------------------------------------
const COLOR_SLUG = {
    "Black":        "black",
    "White":        "white",
    "Navy Blue":    "navy",
    "Heather Grey": "heather",
    "Forest Green": "forest",
};

function mockupUrl(productId, color) {
    const slug = COLOR_SLUG[color];
    if (!slug) return null;
    return `/mockup-${productId}-${slug}.png`;
}

// -----------------------------------------------------------
// Single-colorway products: the underlying product photo is
// identical across every "color" the backend lists (same file
// duplicated per color slug), so offering a color choice for
// these is meaningless and any color label shown next to them
// can end up contradicting the actual photo (e.g. a natural-canvas
// tote labeled "Black"). The color picker + any color label is
// hidden entirely for these product IDs.
// -----------------------------------------------------------
const SINGLE_COLORWAY_IDS = new Set([
    "mug", "stickers", "tote", "bottle", "pin", "poster", "journal", "deck",
]);

// -----------------------------------------------------------
// Local CSS product card — no external image dependency.
// Used as the fallback whenever a real mockup isn't available
// for the selected product+color combo.
// -----------------------------------------------------------
function ProductArtwork({ productName, color }) {
    const swatch = COLOR_SWATCH[color] || COLOR_SWATCH["Black"];
    const isLight = color === "White" || color === "Heather Grey";
    return (
        <div
            className="absolute inset-0 flex flex-col items-center justify-center transition-colors duration-500"
            style={{ background: swatch.bg, color: swatch.ink }}
            data-testid="product-artwork"
        >
            {/* Decorative diagonal band for visual interest */}
            <div
                className="absolute inset-0 opacity-10"
                style={{
                    background: `repeating-linear-gradient(135deg, ${swatch.ink} 0 1px, transparent 1px 22px)`,
                }}
            />
            {/* Subtle radial highlight top-left */}
            <div
                className="absolute inset-0 opacity-25"
                style={{
                    background: `radial-gradient(circle at 30% 25%, ${isLight ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.15)"}, transparent 55%)`,
                }}
            />
            <div className="relative z-10 text-center px-6">
                <div className="font-display text-2xl md:text-3xl font-bold leading-tight tracking-tight">
                    {productName}
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.25em] opacity-80">
                    {color}
                </div>
            </div>
        </div>
    );
}

// -----------------------------------------------------------
// ProductMedia: swaps to a real mockup image when the current
// product+color combo has one, otherwise renders ProductArtwork.
// Uses a 1-second safety timeout so the image is guaranteed to
// appear even if onLoad never fires, and falls back to the CSS
// placeholder on any load failure.
// -----------------------------------------------------------
const TWO_PANEL_PRODUCT_IDS = new Set([
    "tee", "tank", "long-sleeve", "crewneck", "light-hoodie", "heavy-hoodie", "zip-hoodie",
    "trucker", "bucket-hat", "tote", "bottle", "journal", "mug",
]);

function mediaTransform(productId) {
    return TWO_PANEL_PRODUCT_IDS.has(productId)
        ? "scale(2.2) translate(30%, 0%)"
        : "scale(1.55) translate(0%, 0%)";
}

function ProductMedia({ productId, productName, color }) {
    const src = mockupUrl(productId, color);
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setLoaded(false);
        setFailed(false);
        if (!src) return;
        // Safety fallback — force visible after 1s even if onLoad never fires
        const t = setTimeout(() => setLoaded(true), 1000);
        return () => clearTimeout(t);
    }, [src]);

    // No mockup available OR image failed to load → CSS placeholder
    if (!src || failed) {
        return <ProductArtwork productName={productName} color={color} />;
    }

    return (
        <>
            {/* Placeholder shows only while image is loading — hidden after load */}
            {!loaded && <ProductArtwork productName={productName} color={color} />}
            <img
                key={src}
                src={src}
                alt={`${productName} in ${color}`}
                className="absolute inset-0 w-full h-full object-contain bg-[#f6f4ee] transition-opacity duration-500"
                // Uniform "front-panel focus" transform for every product card:
                // zooms on the FRONT view of the mockup (which lives in the
                // LEFT half of every base photo for two-panel flat-lays, and
                // roughly-centered for single-mannequin shots). Shifting the
                // image RIGHT via a positive translate slides the left-half
                // front-view into the visible clip window; the parent card's
                // overflow-hidden crops away the back-view/right-half so the
                // star logo and any front-of-garment print land at a
                // consistent visual size across the whole grid regardless of
                // how much empty studio background each source photo carried.
                // scale(2.2) + translate(30%, 0%): the previous 1.55x/15%
                // combo left a sliver of the back-view garment visible at
                // the card's right edge (ticket 259407) because 15% of the
                // element's own box, not the zoomed visual size, isn't
                // enough lateral shift once magnified — the boundary between
                // the two garments (near the image's 50% seam) stayed just
                // inside the visible window. This stronger crop pushes that
                // seam fully out of frame with margin to spare.
                style={{
                    opacity: loaded ? 1 : 0,
                    transform: mediaTransform(productId),
                    transformOrigin: "50% 50%",
                }}
                onLoad={() => setLoaded(true)}
                onError={() => setFailed(true)}
                data-testid={`product-${productId}-image`}
            />
        </>
    );
}

export default function Shop() {
    const [catalog, setCatalog] = useState([]);
    const [counts, setCounts] = useState({ per_product: {}, total: 0 });

    useEffect(() => {
        api.get("/merch/catalog")
            .then((r) => setCatalog(r.data.products || []))
            .catch(() => setCatalog([]));
        api.get("/merch/preorders/count")
            .then((r) => setCounts(r.data || { per_product: {}, total: 0 }))
            .catch(() => {});
    }, []);

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
            <SEO
                title="Merch shop — Wear the truth. Fund the mission."
                description="Real-Check merch: premium tees, hoodies, mugs, stickers, caps, and more with the Real-Check compass-star logo. Every pre-order splits 50/50 to Hands of Hope."
                path="/shop"
                keywords="real-check merch, fact checker shirt, wear the truth, hoodie fact check, hands of hope charity"
            />
            {/* HERO */}
            <section className="mb-14 relative">
                {/* ambient teal glow */}
                <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl -z-0" />
                <div className="pointer-events-none absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-emerald-400/15 blur-3xl -z-0" />
                <div className="grid lg:grid-cols-5 gap-10 items-center relative z-10">
                    <div className="lg:col-span-3">
                        <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-[11px] font-semibold text-accent mb-5">
                            <BellRing className="h-3 w-3 text-primary" /> Founding drop · Pre-orders open
                        </div>
                        <h1 className="font-display text-4xl md:text-6xl text-accent tracking-tight leading-[1.03]">
                            Wear the <span className="text-primary">truth</span>.<br />
                            Fund the mission.
                        </h1>
                        <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
                            {catalog.length || 20} pieces — apparel, headwear, everyday carry, and collectors. Every pre-order splits <span className="text-accent font-semibold">50 / 50</span>: half funds
                            Hands of Hope, half keeps Real-Check ad-free and independent.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="#lineup">
                                <Button className="rounded-full h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/30" data-testid="shop-see-lineup-btn">
                                    Shop the lineup ↓
                                </Button>
                            </a>
                            <Link to="/donate">
                                <Button variant="secondary" className="rounded-full h-12 px-6" data-testid="shop-donate-btn">
                                    <Heart className="h-4 w-4 mr-2" /> Donate directly instead
                                </Button>
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-2 relative">
                        <div className="aspect-square rounded-3xl overflow-hidden border border-primary/30 badge-glow-primary bg-[#041a1e] shadow-2xl shadow-primary/20">
                            <img
                                src="/merch-hero.png"
                                alt="Real-Check merch — hero shot"
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.classList.add("flex", "items-center", "justify-center"); }}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* CAMPAIGN PROGRESS — Kickstarter-style urgency mechanic */}
            <CampaignProgress totalPreorders={counts.total} />

            {/* SPLIT INFOGRAPHIC + FOUNDING MEMBER BADGE */}
            <div className="grid md:grid-cols-2 gap-5 mb-16">
                <Card className="card-surface p-6 md:p-7 rounded-2xl">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
                            <Heart className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-display text-lg text-accent">50 / 50 forever</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                                Every sticker, mug, tee, and hoodie splits down the middle — half to Hands of Hope,
                                half to keep Real-Check independent. No bait-and-switch.
                            </p>
                            <Link to="/transparency" className="text-xs text-primary underline underline-offset-4 mt-2 inline-block">
                                See where the money goes →
                            </Link>
                        </div>
                    </div>
                </Card>
                <Card className="card-surface p-6 md:p-7 rounded-2xl relative overflow-hidden">
                    <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-amber-400/20 blur-3xl" />
                    <div className="flex items-start gap-4 relative">
                        <div className="w-12 h-12 rounded-2xl bg-amber-400/20 border border-amber-400/50 flex items-center justify-center shrink-0">
                            <Star className="h-5 w-5 text-amber-300 fill-amber-300" />
                        </div>
                        <div>
                            <h3 className="font-display text-lg text-accent flex items-center gap-2">
                                Founding drop
                                <span className="text-[10px] uppercase tracking-widest bg-amber-400/20 text-amber-200 border border-amber-400/40 px-2 py-0.5 rounded-full">limited</span>
                            </h3>
                            <p className="text-sm text-muted-foreground leading-relaxed mt-1">
                                First-run production is <strong className="text-accent">numbered</strong>. Every founding order
                                ships with a signed thank-you card from Chris, direct from the Hands of Hope mission.
                            </p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* LINEUP */}
            <section id="lineup">
                <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-primary mb-2">The lineup</p>
                        <h2 className="font-display text-3xl md:text-4xl text-accent">{catalog.length || 20} pieces. One truth.</h2>
                    </div>
                    <p className="text-sm text-muted-foreground max-w-sm">
                        Pre-order now — locked-in price, first-batch shipping. Half of every order funds Hands of Hope.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {catalog.map((p, i) => (
                        <ProductCard
                            key={p.id}
                            product={p}
                            index={i}
                            preorderCount={counts.per_product[p.id] || 0}
                        />
                    ))}
                </div>
            </section>

            {/* FOOTER CTA */}
            <section className="mt-20 text-center">
                <p className="text-muted-foreground text-sm mb-4">Prefer to give directly?</p>
                <Link to="/donate">
                    <Button className="rounded-full h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="shop-bottom-donate-btn">
                        <Heart className="h-4 w-4 mr-2" /> Donate to Hands of Hope directly
                    </Button>
                </Link>
            </section>
        </div>
    );
}

// -----------------------------------------------------------
// Kickstarter-style pre-order goal bar. Creates real urgency:
// "First production run triggers at 100 pre-orders — 47 to go."
// -----------------------------------------------------------
const CAMPAIGN_GOAL = 100;
function CampaignProgress({ totalPreorders }) {
    const raw = totalPreorders || 0;
    const pct = Math.min(100, Math.round((raw / CAMPAIGN_GOAL) * 100));
    const remaining = Math.max(0, CAMPAIGN_GOAL - raw);
    const hit = raw >= CAMPAIGN_GOAL;

    return (
        <Card className="card-surface p-6 md:p-7 rounded-2xl mb-6 relative overflow-hidden" data-testid="campaign-progress">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-emerald-500/10 pointer-events-none" />
            <div className="relative">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                        <Flame className="h-5 w-5 text-orange-400" />
                        <h3 className="font-display text-lg md:text-xl text-accent">
                            {hit ? "Goal hit — first run in production!" : "First-run production goal"}
                        </h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                        <span className="text-2xl md:text-3xl font-display font-bold text-primary" data-testid="campaign-progress-count">
                            {raw}
                        </span>{" "}
                        / {CAMPAIGN_GOAL} pre-orders
                    </p>
                </div>
                {/* Progress bar */}
                <div className="h-3 w-full rounded-full bg-white/5 overflow-hidden border border-white/10">
                    <div
                        className="h-full bg-gradient-to-r from-primary via-emerald-400 to-primary transition-all duration-700"
                        style={{ width: `${pct}%`, boxShadow: "0 0 20px rgba(34, 224, 180, 0.5)" }}
                        data-testid="campaign-progress-bar"
                    />
                </div>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                    {hit
                        ? "Thank you — the first Real-Check merch batch is heading to production. New pre-orders will ship in the next batch."
                        : <>Just <strong className="text-accent">{remaining} more</strong> pre-orders unlocks the first production run. Founding orders ship first, signed by Chris.</>
                    }
                </p>
            </div>
        </Card>
    );
}

function ProductCard({ product, index, preorderCount }) {
    const meta = PRODUCT_META[product.id] || {};
    const isSingleColorway = SINGLE_COLORWAY_IDS.has(product.id);
    const [color, setColor] = useState(product.colors?.[0] || "Black");
    const [size, setSize] = useState(product.sizes?.[0] || "");
    const [quantity, setQuantity] = useState(1);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const preorder = async (e) => {
        e.preventDefault();
        const cleanEmail = (email || "").trim();
        if (!cleanEmail.includes("@") || cleanEmail.length < 5) {
            toast.error("Enter a valid email");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/merch/preorder", {
                product_id: product.id,
                color,
                size,
                quantity,
                email: cleanEmail,
                name: name || null,
                origin_url: window.location.origin,
            });
            if (data.url) {
                window.location.href = data.url;
            } else {
                toast.error("Could not start checkout");
                setLoading(false);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start pre-order");
            setLoading(false);
        }
    };

    const total = ((product.price || 0) * quantity).toFixed(2);
    const swatch = COLOR_SWATCH[color] || COLOR_SWATCH["Black"];

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            data-testid={`product-card-${product.id}`}
        >
            <Card className="card-surface rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 h-full flex flex-col group">
                {/* Product artwork — real mockup image with hover zoom, CSS fallback otherwise */}
                <div className="relative aspect-square overflow-hidden border-b border-white/5">
                    <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-[1.04]">
                        <ProductMedia productId={product.id} productName={product.name} color={color} />
                    </div>

                    {/* Top-corner badges */}
                    {meta.badge && (
                        <span className="absolute top-3 left-3 z-20 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-primary text-primary-foreground shadow-lg">
                            {meta.badge}
                        </span>
                    )}
                    <span className="absolute top-3 right-3 z-20 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/20 backdrop-blur border border-emerald-400/40 text-emerald-300">
                        Pre-order
                    </span>
                    {preorderCount > 0 && (
                        <span className="absolute bottom-3 left-3 z-20 text-[10px] font-semibold px-2 py-1 rounded-full bg-black/70 backdrop-blur border border-white/10 text-emerald-300" data-testid={`product-${product.id}-count`}>
                            🔥 {preorderCount} sold
                        </span>
                    )}
                    {/* Selected color badge overlaid on artwork — single-colorway products
                        have no meaningful color choice, so no color label is shown. */}
                    {!isSingleColorway && (
                        <span
                            className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-full bg-black/70 backdrop-blur border border-white/10 text-white"
                            data-testid={`product-${product.id}-selected-color`}
                        >
                            <span
                                className="w-2.5 h-2.5 rounded-full border border-white/40"
                                style={{ background: swatch.bg }}
                            />
                            {color}
                        </span>
                    )}
                </div>

                {/* Product info */}
                <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-3 mb-1">
                        <h3 className="font-display text-xl text-accent leading-tight">{product.name}</h3>
                        <span className="font-display font-bold text-primary text-lg shrink-0" data-testid={`product-${product.id}-price`}>
                            ${product.price?.toFixed?.(0) || "—"}
                        </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2 italic">{meta.tagline || ""}</p>
                    {product.material && (
                        <p className="text-xs text-muted-foreground/85 leading-relaxed mb-4" data-testid={`product-${product.id}-material`}>
                            {product.material}
                        </p>
                    )}

                    {!expanded ? (
                        <div className="mt-auto space-y-2">
                            {/* Color swatch mini-row shown even when collapsed so user can preview colors —
                                skipped for single-colorway products (the photo never changes). */}
                            {!isSingleColorway && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    {product.colors?.map((c) => {
                                        const s = COLOR_SWATCH[c];
                                        if (!s) return null;
                                        const selected = color === c;
                                        return (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() => setColor(c)}
                                                title={c}
                                                aria-label={`Preview ${c}`}
                                                data-testid={`product-${product.id}-swatch-${c.replace(/\s/g, "-")}`}
                                                className={`w-6 h-6 rounded-full transition-transform ${selected ? "ring-2 ring-primary ring-offset-2 ring-offset-card scale-110" : "hover:scale-105"}`}
                                                style={{ background: s.bg, border: "1px solid rgba(255,255,255,0.15)" }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                            <Button
                                onClick={() => setExpanded(true)}
                                className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                                data-testid={`product-${product.id}-preorder-cta`}
                            >
                                <ShoppingBag className="h-4 w-4 mr-2" /> Pre-order ${product.price?.toFixed?.(0)}
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={preorder} className="mt-auto space-y-2.5" data-testid={`product-${product.id}-preorder-form`}>
                            {/* Color chips with swatch dots — not shown for single-colorway products,
                                where every "color" option is the same photo/product. */}
                            {!isSingleColorway && (
                                <div>
                                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Color</label>
                                    <div className="flex flex-wrap gap-1.5 mt-1">
                                        {product.colors?.map((c) => {
                                            const s = COLOR_SWATCH[c];
                                            const selected = color === c;
                                            return (
                                                <button
                                                    key={c}
                                                    type="button"
                                                    onClick={() => setColor(c)}
                                                    data-testid={`product-${product.id}-color-${c.replace(/\s/g, "-")}`}
                                                    className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-1 rounded-full border transition-colors ${
                                                        selected
                                                            ? "border-primary bg-primary/20 text-accent"
                                                            : "border-white/10 text-muted-foreground hover:border-white/30"
                                                    }`}
                                                >
                                                    {s && (
                                                        <span
                                                            className="w-2.5 h-2.5 rounded-full"
                                                            style={{ background: s.bg, border: "1px solid rgba(255,255,255,0.2)" }}
                                                        />
                                                    )}
                                                    {c}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {/* Size chips */}
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Size</label>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                    {product.sizes?.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => setSize(s)}
                                            data-testid={`product-${product.id}-size-${s.replace(/\W/g, "-")}`}
                                            className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                                                size === s
                                                    ? "border-primary bg-primary/20 text-accent"
                                                    : "border-white/10 text-muted-foreground hover:border-white/30"
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Qty */}
                            <div className="flex items-center gap-2">
                                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Qty</label>
                                <div className="flex items-center border border-white/10 rounded-full overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                                        className="px-2 text-accent hover:bg-white/5"
                                        data-testid={`product-${product.id}-qty-minus`}
                                        aria-label="decrease"
                                    >−</button>
                                    <span className="w-8 text-center text-sm text-accent" data-testid={`product-${product.id}-qty-value`}>{quantity}</span>
                                    <button
                                        type="button"
                                        onClick={() => setQuantity((q) => Math.min(10, q + 1))}
                                        className="px-2 text-accent hover:bg-white/5"
                                        data-testid={`product-${product.id}-qty-plus`}
                                        aria-label="increase"
                                    >+</button>
                                </div>
                                <span className="text-xs text-muted-foreground ml-auto">Total <strong className="text-accent">${total}</strong></span>
                            </div>
                            <Input
                                type="text"
                                inputMode="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@email.com"
                                className="bg-white/5 border-white/10 h-9 text-sm"
                                data-testid={`product-${product.id}-email-input`}
                            />
                            <Input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Name (optional)"
                                className="bg-white/5 border-white/10 h-9 text-sm"
                                data-testid={`product-${product.id}-name-input`}
                            />
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid={`product-${product.id}-checkout-button`}
                                className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold"
                            >
                                {loading ? (
                                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
                                ) : (
                                    <><ShoppingBag className="h-4 w-4 mr-2" /> Pre-order · ${total}</>
                                )}
                            </Button>
                            <p className="text-[10px] text-muted-foreground text-center">
                                Secure checkout via Stripe · 50% funds Hands of Hope
                            </p>
                        </form>
                    )}
                </div>
            </Card>
        </motion.div>
    );
}
